import { faCheckCircle, faInfoCircle, faXmarkCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createContext, useCallback, useContext, useEffect, useMemo, useTransition } from "react";
import { usePaginationFragment, usePreloadedQuery, useQueryLoader } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";

import { Button, Input, Paragraph, Tooltip, useDebouncedState } from "@skydio/rivet-ui";

import { legacyToRivetPagination, useRelayPagination } from "components/common/Pagination";
import PaginatedTable from "components/common/table/PaginatedTable";
import { getUserRoles } from "components/main/team/TeamView";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useQueryStringState } from "hooks/useQueryStringState";
import { formatOrgPrefixedPath } from "utils/common";

import AdminSearchEmptyState from "./AdminSearchEmptyState";

import styles from "../AdminSearchPage.module.scss";

import type { ColumnType } from "antd/lib/table";
import type { PreloadedQuery } from "react-relay";
import type { ConnectionNodeType } from "@skydio/relay-toolkit";
import type {
  UsersList_users$data,
  UsersList_users$key,
} from "__generated__/UsersList_users.graphql";
import type { UsersListAdminQuery } from "__generated__/UsersListAdminQuery.graphql";
import type { UsersRefetchQuery } from "__generated__/UsersRefetchQuery.graphql";

export const rootQuery = graphql`
  query UsersListAdminQuery($count: Int!, $cursor: String, $searchTerm: String) {
    ...UsersList_users
  }
`;

const usersFragment = graphql`
  fragment UsersList_users on Query @refetchable(queryName: "UsersRefetchQuery") {
    users(searchTerm: $searchTerm, first: $count, after: $cursor)
      @connection(key: "UsersList_users") {
      totalCount
      edges {
        node {
          id
          uuid
          email
          emailStatus
          active
          enabled
          loginDefault
          displayName
          organizationPermission
          organizationId
          organization {
            id
            name
          }
          resourceGroupLinks {
            edges {
              node {
                id
                resourceGroupId
                resourceGroupName
                resourceGroupPermission
              }
            }
          }
          groups {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export interface TableProps {
  queryReference: PreloadedQuery<UsersListAdminQuery>;
}

const PAGE_SIZE = 10;
export type User = ConnectionNodeType<UsersList_users$data["users"]>;

export interface UsersPageContextType {
  setCount?: (count: number | undefined) => void;
}
export const UsersPageContext = createContext<UsersPageContextType | null>(null);
const UsersList: React.FC<{
  queryReference: PreloadedQuery<UsersListAdminQuery>;
}> = ({ queryReference }) => {
  const usersPageContext = useContext(UsersPageContext);
  const supportUserExpiryEnabled = useFeatureFlag("supportUserExpiry");

  const queryData = usePreloadedQuery<UsersListAdminQuery>(rootQuery, queryReference);

  const [searchTerm, setSearchTerm] = useQueryStringState("search", "");
  const [localSearchTerm, setLocalSearchTerm] = useQueryStringState("ulsearch", "");

  useEffect(() => {
    if (localSearchTerm) {
      setSearchTerm("");
    }
  }, [localSearchTerm, setSearchTerm]);

  const [userInput, handleUserInput] = useDebouncedState<string>(
    localSearchTerm,
    setLocalSearchTerm
  );

  const paginationQuery = usePaginationFragment<UsersRefetchQuery, UsersList_users$key>(
    usersFragment,
    queryData
  );

  const [paginationProps, currentPageEdges, resetPage] = useRelayPagination(
    paginationQuery,
    PAGE_SIZE,
    paginationQuery.data?.users
  );

  const { refetch } = paginationQuery;
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      resetPage();

      let count = PAGE_SIZE;
      if (!localSearchTerm && !searchTerm) count = 0;
      refetch({
        count: count,
        searchTerm: localSearchTerm ? localSearchTerm : searchTerm, // Prefer local searchTerm
      });
    });
  }, [refetch, resetPage, searchTerm, localSearchTerm]);

  const currentPageNodes = currentPageEdges.map(edge => edge.node).filter(node => node !== null);

  const resetFilters = useCallback(() => {
    setLocalSearchTerm("");
    setSearchTerm("");
    handleUserInput("");
  }, [handleUserInput, setLocalSearchTerm, setSearchTerm]);

  const getColumns = useCallback((): ColumnType<User>[] => {
    return [
      {
        title: "Name",
        render: (user: User) => (
          <Paragraph>
            {user.organizationId ? (
              <Link
                to={formatOrgPrefixedPath(
                  user.organizationId,
                  `/settings/team?userId=${user.uuid}`
                )}
              >
                {user.displayName}
              </Link>
            ) : (
              user.displayName
            )}
            <div className="text-xs">{user.email}</div>
            <div className="text-xs">{user.uuid}</div>
          </Paragraph>
        ),
      },
      {
        title: "Organization",
        render: (user: User) => (
          <Paragraph>
            {user.organizationId && (
              <Link to={formatOrgPrefixedPath(user.organizationId, "/settings/general")}>
                {user.organization?.name}
              </Link>
            )}
          </Paragraph>
        ),
      },
      {
        title: (
          <>
            {"Access "}
            <Tooltip
              color="default"
              content="Granular permissions of the user to specific resource groups"
              showArrow
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </>
        ),
        render: (user: User) => (
          <Paragraph>
            {user.organizationId &&
              user.organization &&
              getUserRoles(
                user,
                user.organization.name,
                user.organizationId,
                supportUserExpiryEnabled
              )}
          </Paragraph>
        ),
      },
      {
        title: (
          <>
            {"Enabled "}
            <Tooltip
              color="default"
              content="User has the ability to log in from Skydio's perspective"
              showArrow
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </>
        ),
        dataIndex: "enabled",
        render: (value: boolean | null) => (
          <div className="flex items-center justify-center">
            {value ? (
              <FontAwesomeIcon icon={faCheckCircle} color="green" />
            ) : (
              <FontAwesomeIcon icon={faXmarkCircle} color="red" />
            )}
          </div>
        ),
      },
      {
        title: (
          <>
            {"Active "}
            <Tooltip color="default" content="User has logged in at least once" showArrow>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </>
        ),
        dataIndex: "active",
        render: (value: boolean | null) => (
          <div className="flex items-center justify-center">
            {value ? (
              <FontAwesomeIcon icon={faCheckCircle} color="green" />
            ) : (
              <FontAwesomeIcon icon={faXmarkCircle} color="red" />
            )}
          </div>
        ),
      },
      {
        title: (
          <>
            {"Default Login "}
            <Tooltip
              color="default"
              content="On initial login to cloud or controller, what cloud org do they land in"
              showArrow
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </>
        ),
        dataIndex: "loginDefault",
        render: (value: boolean | null) => (
          <div className="flex items-center justify-center">
            {value ? (
              <>
                <FontAwesomeIcon icon={faCheckCircle} color="green" />
                {value}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faXmarkCircle} color="red" />
                {value}
              </>
            )}
          </div>
        ),
      },
      {
        title: (
          <>
            {"Groups "}
            <Tooltip
              color="default"
              content="An amalgum of permission groups, feature flags, and release groups"
              showArrow
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </>
        ),
        render: (user: User) => (
          <Paragraph>{user.groups?.edges.map(group => group?.node?.name).join(", ")}</Paragraph>
        ),
      },
    ];
  }, [supportUserExpiryEnabled]);
  const columns = useMemo(() => getColumns(), [getColumns]);

  useEffect(() => {
    if (usersPageContext?.setCount) {
      usersPageContext?.setCount(
        paginationQuery.data.users?.edges.length ? paginationProps.totalCount : 0
      );
    }
  }, [
    usersPageContext,
    usersPageContext?.setCount,
    paginationProps.totalCount,
    paginationQuery.data.users?.edges.length,
  ]);

  return (
    <>
      <div className="flex w-full gap-4 pl-1">
        <Input
          placeholder="User Email/UUID"
          className={styles.search}
          value={userInput}
          onValueChange={handleUserInput}
          isClearable
        />
        <Button onClick={resetFilters} title="Reset filters">
          Reset
        </Button>
      </div>
      {!paginationQuery.data.users || paginationQuery.data.users.edges.length == 0 ? (
        <AdminSearchEmptyState
          hasResults={paginationProps.totalCount != 0}
          hasFilters={true}
          isLoading={isPending}
        />
      ) : (
        <PaginatedTable
          paginationProps={legacyToRivetPagination(paginationProps, resetPage)}
          columns={columns}
          dataSource={currentPageNodes}
          loading={isPending}
        />
      )}
    </>
  );
};

export const UsersPage = () => {
  const [queryReference, fetchUsers] = useQueryLoader<UsersListAdminQuery>(rootQuery);
  useEffect(() => {
    fetchUsers({ count: 0 });
  }, [fetchUsers]);

  return queryReference && <UsersList queryReference={queryReference} />;
};

export default UsersList;
